'use client';

import type { LoginPayload } from 'src/api/requests';
import type { LoginResponse } from 'src/api/responses';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useState, useEffect } from 'react';
import { QUERY_KEYS } from 'src/constants/queries';
import { TOKEN_KEY, VERIFICATION_STATUS, VERIFICATION_PENDING_KEY } from 'src/constants/session';
import { REDIRECTS, ROUTES } from 'src/constants/routes';
import { extractDataFromToken } from 'src/utils/tokens';
import { GenericErrorResponse } from 'src/api/responses';
import { Auth } from 'src/api/Auth';
import useApi from './useApi';
import useAppStorage from './useAppStorage';
import useCookies from './useCookies';
interface AuthResponse extends Response {
  data: LoginResponse;
  error?: unknown;
}
interface AuthErrorResponse extends Response {
  data?: null;
  error: GenericErrorResponse;
}
const providers = {
  GOOGLE: 'google',
  BASIC: 'basic'
} as const;
const getTokenExpiration = (token: string): number => {
  const payload = extractDataFromToken(token);
  if (!payload || typeof payload !== 'object' || !('exp' in payload)) {
    throw new Error('Invalid token payload: missing exp field');
  }
  const exp = payload.exp;
  return exp;
};
const isVerificationPending = (data: LoginResponse): boolean => {
  if (!data || typeof data !== 'object') return false;
  if (!('verification_status' in data) || typeof data.verification_status !== 'string') return false;
  return data.verification_status === VERIFICATION_STATUS.PENDING_EMAIL_VERIFICATION;
};
const isAuthResponse = (response: unknown): response is AuthResponse => {
  if (typeof response !== 'object' || response === null) return false;
  if (!('data' in response)) return false;
  const data = (response as AuthResponse).data;
  if (typeof data !== 'object' || data === null) return false;
  if (!('success' in data) || typeof data.success !== 'boolean') return false;
  if (!('jwt' in data) || typeof data.jwt !== 'string') return false;
  if (!('status' in data) || typeof data.status !== 'string') return false;
  return true;
};
const isGenericErrorResponse = (response: unknown): response is GenericErrorResponse => {
  if (typeof response !== 'object' || response === null) return false;
  if (!('success' in response) || typeof (response as GenericErrorResponse).success !== 'boolean' || response.success) return false;
  if (!('error' in response)) return false;
  if (typeof response.error !== 'object' || response.error === null) return false;
  return true;
};
const isAuthErrorResponse = (response: unknown): response is AuthErrorResponse => {
  if (typeof response !== 'object' || response === null) return false;
  if (!('error' in response)) return false;
  return true;
};
const handleGlobalAuthError = () => {
  // Clear app state and show notification
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname + window.location.search;
    const logoutUrl = `${REDIRECTS.LOGOUT}?redirect=${encodeURIComponent(currentPath)}`;
    window.location.replace(logoutUrl);
  }
};
const useAuth = () => {
  const {
    getItem,
    setItem,
    clearAll
  } = useAppStorage();
  const {
    setCookie
  } = useCookies();
  const [loginError, setLoginError] = useState<string | null>(null);
  const authClient = useApi(Auth);
  const storedToken = getItem(TOKEN_KEY) as string | null;
  const [token, setToken] = useState<string | null>(storedToken);
  const [isAuthenticated, setIsAuthenticated] = useState(!!storedToken);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    mutateAsync: login,
    isPending: isLoginPending,
    isError: isLoginError
  } = useMutation({
    mutationKey: [QUERY_KEYS.AUTH.LOGIN],
    mutationFn: async ({
      email,
      password
    }: LoginPayload) => {
      setIsLoading(true);
      setLoginError(null);
      try {
        const response = await authClient.basicSignIn({
          email,
          password
        });
        if (isAuthResponse(response) && response.data.success) {
          const {
            data
          } = response;
          await setCookie({
            name: VERIFICATION_PENDING_KEY,
            value: `${isVerificationPending(data) ? 1 : 0}`
          });
          if (data.jwt) {
            authWithToken(data.jwt);
          } else {
            throw new Error('Login response missing JWT token');
          }
        } else {
          console.error('Login failed:', response.error || 'Invalid response format');
        }
      } catch (errorResponse) {
        if (isAuthErrorResponse(errorResponse) && isGenericErrorResponse(errorResponse.error)) {
          const {
            error = {
              message: 'Authentication error'
            }
          } = errorResponse.error;
          setLoginError(error.message);
          setIsLoading(false);
          return;
        }
        throw errorResponse;
      }
    },
    onError: error_0 => {
      console.error('Authentication error:', error_0);
      setIsLoading(false);
      handleGlobalAuthError();
    }
  });

  // TODO: Define responsibility of token keeping (session vs auth)
  const getToken = useCallback((): string => `Bearer ${token}`, [token]);
  const loginWithGoogle = useCallback(async () => {
    const response_0 = await authClient.socialSignIn({
      provider: 'google',
      return_uri: window.location.origin + ROUTES.SOCIAL_LOGIN_LANDING
    });
    if (response_0.ok && response_0.data.authorization_uri) {
      setItem('auth_session', response_0.data.token);
      window.location.assign(response_0.data.authorization_uri);
    }
  }, [authClient, setItem]);
  const signupWithGoogle = useCallback(async () => {
    const response_1 = await authClient.socialSignIn({
      provider: 'google',
      return_uri: window.location.origin + ROUTES.ONBOARDING_SOCIAL_LANDING
    });
    if (response_1.ok && response_1.data.authorization_uri) {
      setItem('auth_session', response_1.data.token);
      window.location.assign(response_1.data.authorization_uri);
    }
  }, [authClient, setItem]);
  const forgotPassword = useCallback(async (email_0: string) => {
    const response_2 = await authClient.forgotPassword(email_0);
    return response_2.ok;
  }, [authClient]);
  const resetPassword = useCallback(async (email_1: string, password_0: string, password_confirmation: string, token_0: string) => {
    const response_3 = await authClient.resetPassword(email_1, password_0, password_confirmation, token_0);
    return response_3.ok;
  }, [authClient]);
  const authWithToken = useCallback(async (token_1: string) => {
    await setCookie({
      name: VERIFICATION_PENDING_KEY,
      value: '0'
    });
    setToken(token_1);
    setItem(TOKEN_KEY, token_1);
    await setCookie({
      name: TOKEN_KEY,
      value: token_1,
      options: {
        maxAge: getTokenExpiration(token_1) - Math.floor(Date.now() / 1000)
      }
    });
    setIsAuthenticated(true);
    setIsLoading(false);
  }, [setCookie, setItem]);
  const logout = useCallback(async () => {
    // hook level authentication state
    setToken(null);
    // these are requests to the local API to flush the cookies.
    await setCookie({
      name: TOKEN_KEY,
      value: '',
      options: {
        maxAge: 0 // Clear cookie immediately
      }
    });
    await setCookie({
      name: VERIFICATION_PENDING_KEY,
      value: '',
      options: {
        maxAge: 0 // Clear cookie immediately
      }
    });

    // app state authentication flag
    setIsAuthenticated(false);

    // clears all items from local storage
    clearAll();
  }, [clearAll, setCookie]);
  useEffect(() => {
    if (!isLoginPending && isAuthenticated) {
      // Auth gating: clears the loading flag once login settles. Route guards read this, so the
      // exact render it flips on matters.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoading(false);
    }
  }, [isLoginPending, isAuthenticated]);
  return {
    providers,
    isAuthenticated,
    login,
    loginError,
    loginWithGoogle,
    signupWithGoogle,
    loginWithToken: authWithToken,
    handleGlobalAuthError,
    getToken,
    logout,
    forgotPassword,
    resetPassword,
    isLoginError,
    isLoading: isLoginPending || isLoading
  };
};
export default useAuth;
export { handleGlobalAuthError };